import * as React from 'react';

import URI from 'jsuri';
import PropTypes from 'prop-types';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import redirect from 'js/lib/coursera.redirect';

import { Box, Button } from '@coursera/coursera-ui';

import { LOGIN_RECAPTCHA_ID, RECAPTCHA_ACTIONS } from 'bundles/authentication/constants';
import type { ReCAPTCHA } from 'bundles/authentication/shared/reCAPTCHA';
import reCAPTCHA from 'bundles/authentication/shared/reCAPTCHA';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import GrowthAcquisitionExperiments from 'bundles/epic/clients/GrowthAcquisition';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { AUTH_FORM_IDS, AUTH_MODES } from 'bundles/user-account/common/constants';
import AuthFormFooter from 'bundles/user-account/components/AuthFormFooter';
import EmailField from 'bundles/user-account/components/EmailField';
import ErrorMessage from 'bundles/user-account/components/ErrorMessage';
import PasswordField from 'bundles/user-account/components/PasswordField';
import VerificationField from 'bundles/user-account/components/VerificationField';
import loginApi from 'bundles/user-account/data/loginApi';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/user-account';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })(Button);

const CURRENT_AUTH_MODE = AUTH_MODES.login;
const EMAIL = AUTH_FORM_IDS.email;
const PASSWORD = AUTH_FORM_IDS.password;
const VERIFICATION_CODE = AUTH_FORM_IDS.verificationCode;

type EventData = {
  namespace: {
    app: string;
    page: string;
  };
};

declare global {
  interface Window {
    _eventData: EventData;
    appName?: string;
  }
}

type State = {
  recaptchaToken: string;
  email: string;
  password: string;
  code: string;
  errorCode: string;
  isLoginBtnDisabled: boolean;
};

type Props = {
  redirectUrl?: string;
  router: InjectedRouter;
};

function getTranslations(): { [name: string]: string } {
  return {
    FORGET_PASSWORD: _t('Forgot password?'),
    LOGIN: _t('Log in'),
  };
}

class LoginForm extends React.Component<Props, State> {
  static contextTypes = {
    _eventData: PropTypes.object.isRequired,
  };

  captcha: ReCAPTCHA | null = null;

  state = {
    email: '',
    password: '',
    code: '',
    recaptchaToken: '',
    errorCode: '',
    isLoginBtnDisabled: false,
  };

  constructor(props: Props, context: $TSFixMe) {
    super(props, context);
    const { router } = props;
    const { email } = router.location.query;
    const prefillEmail = email && decodeURI(email);
    if (prefillEmail) this.state.email = prefillEmail;
  }

  async componentDidMount() {
    const { _eventData } = this.context;

    const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaLogin');

    this.captcha = reCAPTCHA(LOGIN_RECAPTCHA_ID, { _eventData, checkbox: isCheckbox, action: RECAPTCHA_ACTIONS.login });
  }

  // Redirects the user to the page they previously were on; in doing this redirect, the global user object is reset
  // and the user then becomes a logged in user
  // @ts-expect-error TS7016 TODO: response needs to be typed
  handleLoginSuccess = (response) => {
    return Instrumentation.login().fin(() => {
      const { redirectUrl, router } = this.props;
      const { pathname, search } = router.location;
      const redirectUrlFromBE = response?.redirectTo;
      let defaultRedirectUrl;
      if (pathname.includes('frontpage-next')) {
        defaultRedirectUrl = new URI('/' + search).deleteQueryParam('authMode').toString();
      } else {
        defaultRedirectUrl = new URI(pathname + search).deleteQueryParam('authMode').toString();
      }

      // Building out a redirectUrl. In doing this we allow the query-parameter and component-
      // parameter custom URLs to be the final say in where a user gets redirected. Otherwise,
      // a redirection url given by a Backend response gets used or, in the event that isn't
      // returned, a default url is then generated re-using the original login location.
      const redirectionLocation = redirectUrl || redirectUrlFromBE || defaultRedirectUrl;

      redirect.setLocation(redirectionLocation);
    });
  };

  // If captcha is enabled, this resets the captcha and sets the errorCode
  // @ts-expect-error TS7016 TODO: response needs to be typed
  handleLoginError = (response) => {
    this.captcha?.reset();

    let errorCode = '';
    if (response) {
      // Use the response text as an error code if BE doesn't return the error in the errorCode field
      errorCode = (response.responseJSON && response.responseJSON.errorCode) || response.responseText;
    }

    this.setState(() => ({
      errorCode,
      isLoginBtnDisabled: false,
    }));
  };

  handleLoginSubmit = (ev: React.FormEvent) => {
    if (ev) {
      ev.preventDefault();
    }

    this.tryLogin();
  };

  onChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = ev.target;

    // @ts-expect-error TS7016 TODO: fix this function incompaticle types
    this.setState(() => ({
      [name]: value,
    }));
  };

  // Handles login submission which in acquiring the recaptcha token
  // internally executes Google's recaptcha. This ensures that if Google wants to display recaptcha
  // it will occur after the submit button is clicked.
  tryLogin = async () => {
    const { email, password, code: verificationCode } = this.state;
    const { redirectUrl } = this.props;
    let loginPayload = {};
    // Disable the login button after the recaptcha has been completed
    const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaLogin');

    const recaptchaToken = await this.captcha?.getResponse();

    // recaptcha will auto-retry if null
    if (recaptchaToken === null) {
      return this.setState(() => ({ errorCode: isCheckbox ? 'recaptchaFailed' : '' }));
    }

    this.setState(() => ({ isLoginBtnDisabled: true }));

    loginPayload = { email, password, recaptchaToken, verificationCode, redirectTo: redirectUrl };

    trackAuthAttempts(window._eventData, 'login');
    loginApi(loginPayload).then(this.handleLoginSuccess).catch(this.handleLoginError);
  };

  render() {
    const { email, password, code: verificationCode, errorCode, isLoginBtnDisabled } = this.state;
    const promptForSecondFactor = errorCode === 'twoFactorRequired' || errorCode === 'twoFactorInvalid';

    const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaLogin');

    let autoFocus: string = PASSWORD;
    if (email === '') {
      autoFocus = EMAIL;
    } else if (promptForSecondFactor) {
      autoFocus = VERIFICATION_CODE;
    }

    const _T = getTranslations();
    return (
      <Box rootClassName="rc-LoginForm" htmlAttributes={{ 'data-e2e': 'login-form-wrapper' }} flexDirection="column">
        {errorCode && <ErrorMessage errorCode={errorCode} id="login-form-error" />}
        {/* setting action to javascript:void(0) because of
        https://stackoverflow.com/questions/1818249/form-with-no-action-and-where-enter-does-not-reload-page */}
        {/* eslint-disable-next-line no-script-url */}
        <form name="login" action="javascript:void(0)">
          <Box rootClassName="w-100 placeholder-contrast" flexDirection="column">
            <div className="m-b-1">
              <EmailField
                value={email}
                aria-describedby="login-form-error"
                aria-required={true}
                aria-invalid={false}
                onChange={this.onChange}
                autoFocus={autoFocus === EMAIL}
                data-e2e="login-email-input"
              />
            </div>
            <div className={promptForSecondFactor ? 'm-b-1' : 'm-b-1s'}>
              <PasswordField
                value={password}
                aria-describedby="login-form-error"
                aria-required={true}
                aria-invalid={false}
                onChange={this.onChange}
                autoFocus={autoFocus === PASSWORD}
                subLabel=""
                data-e2e="login-password-input"
              />
            </div>
            {promptForSecondFactor && (
              <VerificationField
                value={verificationCode}
                aria-describedby="login-form-error"
                aria-required={true}
                aria-invalid={false}
                // @ts-expect-error TS7016 TODO: onChange[has incompatible types
                onChange={this.onChange}
                autoFocus={autoFocus === VERIFICATION_CODE}
                data-e2e="login-two-factor-input"
              />
            )}
            <div id="recaptcha-login" data-size={!isCheckbox && 'invisible'} />
            <div className="text-xs-right m-b-1">
              <TrackedA
                trackingName="login_form_forgot_password_link"
                href="/reset"
                data-js="user-modal-nav-link forgot-password-helper login-password-phase"
                className="fw-bold"
              >
                {_T.FORGET_PASSWORD}
              </TrackedA>
            </div>
            <TrackedButton
              trackingName="login_form_submit_button"
              disabled={isLoginBtnDisabled}
              onClick={this.handleLoginSubmit}
              rootClassName="w-100"
              type="primary"
              label={_T.LOGIN}
              htmlAttributes={{
                'data-js': 'submit',
                'data-e2e': 'login-form-submit-button',
              }}
            />
          </Box>
          <AuthFormFooter authMode={CURRENT_AUTH_MODE} />
        </form>
      </Box>
    );
  }
}

export default compose<Props, Props>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'user_account',
      page: 'login',
    },
  })),
  connectToRouter((router) => {
    return { router };
  })
)(LoginForm);
